import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  Lening,
  LeningAanwending,
  LeningGeldVerstrekker
} from '@snelstart-ventures/fiscaal/domain-logic';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../gedeelde-componenten/gedeelde-componenten.component';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from '@snelstart/ui-icon';

type LeningFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  rekeningNummer: FormControl<string | null>;
  landCode: FormControl<string | null>;
  wozNummerWaarLeningBijHoort: FormControl<string | null>;
  percentageBox1: FormControl<number | null>;
  hoofdsom0101: FormControl<number | null>;
  hoofdsom3112: FormControl<number | null>;
  geldVerstrekker: FormControl<string | null>;
  aanwending: FormControl<string | null>;
  bijzondereWaardevermindering: FormControl<number | null>;
  rente: FormControl<number | null>;
  berekendRendement: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box3-leningen',
  templateUrl: './box3-leningen.component.html',
  styleUrls: ['./box3-leningen.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiDialogModule,
        SharedUiIconModule
    ]
})
export class Box3LeningenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  protected readonly LeningAanwending = LeningAanwending;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();

  public dialogVisible = false;
  public index = 0;
  public jaar: any;
  public heeftOWR: any;

  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;
  public optionsOnroerendeZaken: SelectFormControlItem<string>[] = [];
  public soortAanwending: SelectFormControlItem<string>[] = [];

  public formGroup = new FormGroup({
    box3Leningen: new FormArray<LeningFormGroup>([])
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entity => {
      this.soortAanwending = [];
      this.formGroup.reset({}, {emitEvent: false});
      this.updateOptionsOnroerendeZaken(entity);
      this.updateForm(entity?.aangifte?.box3?.leningen || []);
      this.updateFormMetPartieleBox1Leningen(entity.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.leningen || []);
      this.heeftOWR = entity.aangifte?.box3?.berekenWerkelijkRendement ?? false;
      this.jaar = entity.year;
      this.soortAanwending.push({value: LeningAanwending.OVERIG, label: 'Overig schulden'});
      this.soortAanwending.push({value: LeningAanwending.WONING_NEDERLAND, label: 'Woning Nederland', disabled: (entity.aangifte?.box3?.overigeOnroerendeZaken?.map(w => w.adres.landCode === 'NLD').length ?? 0) === 0 });
      this.soortAanwending.push({value: LeningAanwending.WONING_BUITENLAND, label: 'Woning buitenland', disabled: (entity.aangifte?.box3?.overigeOnroerendeZaken?.map(w => w.adres.landCode !== 'NLD').length ?? 0) === 0 });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () => this.box3Leningen.getRawValue().map(item => ({
      bedrag: item.bedrag,
      hoofdsom3112: item.hoofdsom3112,
      rente: item.rente,
      bijzondereWaardevermindering: item.bijzondereWaardevermindering,
      wozNummerWaarLeningBijHoort: item.wozNummerWaarLeningBijHoort
    }));

    this.changeSubscriptions.add(
      this.box3Leningen.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractInputForCalculation()),
        startWith(extractInputForCalculation()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  showDialog(index: number): void {
    this.index = index;
    this.dialogVisible = true;
  }

  get box3Leningen(): FormArray<LeningFormGroup> {
    return this.formGroup.controls.box3Leningen;
  }

  addRij(): void {
    this.box3Leningen.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.box3Leningen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
    this.subscribeToChanges();
  }

  createFormGroup(lening?: Lening): LeningFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(lening?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(lening?.bedrag ?? null),
      rekeningNummer: new FormControl<string | null>(lening?.rekeningNummer ?? null),
      landCode: new FormControl<string | null>(lening?.landCode ?? null),
      wozNummerWaarLeningBijHoort: new FormControl<string | null>(lening?.wozNummerWaarLeningBijHoort != null ? lening.wozNummerWaarLeningBijHoort : "0"),
      percentageBox1: new FormControl<number | null>(lening?.percentageBox1 ?? 0),
      hoofdsom0101: new FormControl<number | null>(lening?.hoofdsom0101 ?? lening?.bedrag ?? null),
      hoofdsom3112: new FormControl<number | null>(lening?.hoofdsom3112 ?? null),
      geldVerstrekker: new FormControl<string | null>((lening?.geldVerstrekker as string | null) ?? 'BANK'),
      aanwending: new FormControl<string | null>((lening?.aanwending as string | null) ?? null),
      bijzondereWaardevermindering: new FormControl<number | null>(lening?.bijzondereWaardevermindering ?? null),
      rente: new FormControl<number | null>(lening?.rente ?? null),
      berekendRendement: new FormControl<number | null>(lening?.rente ?? null)
    }, {validators: this.box3WoningValidator()});
  }

  public verwijderLetters(input: string): string {
    return input.replace(/\D/g, '');
  }

  private updateOptionsOnroerendeZaken(entity: IncomeTaxEntity): void {
    const zaken = entity.aangifte?.box3?.overigeOnroerendeZaken ?? [];

    this.optionsOnroerendeZaken = zaken.flatMap((zaak, index) => {
      const adres = zaak?.adres;
      if (!adres?.postcode) {
        return [];
      }

      const postcode = this.verwijderLetters(adres.postcode ?? '1111');
      const huisnummer = String(adres.huisNummer ?? '2222');
      const woz = String(zaak?.woz ?? '3333');
      const woznummerValue = `${index}${postcode}${huisnummer}${woz}`
        .replace(/\D/g, '')
        .slice(0, 12)
        .padStart(12, '0');

      return [{
        value: woznummerValue,
        label: `${adres.postcode} - ${adres.huisNummer}${adres.huisNummerToevoeging ? ` - ${adres.huisNummerToevoeging}` : ''}`
      }];
    });
  }

  private updateForm(data: Lening[]): void {
    const lijst = data || [];
    const formArray = this.box3Leningen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue({
          ...lijst[i],
          wozNummerWaarLeningBijHoort: lijst[i].wozNummerWaarLeningBijHoort != null
            ? String(lijst[i].wozNummerWaarLeningBijHoort)
            : null
        }, {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }
  }

  private updateFormMetPartieleBox1Leningen(data: Lening[]): void {
    const items: any[] = data
      .filter(item => (item.percentageBox1 == null || item.percentageBox1 < 100))
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        rekeningNummer: item.rekeningNummer ?? undefined,
        hoofdsom0101: item.hoofdsom0101 ? Math.round(item.hoofdsom0101 * (100 - item.percentageBox1) / 100) : undefined,
        bedrag: item.hoofdsom0101 ? Math.round(item.hoofdsom0101 * (100 - item.percentageBox1) / 100) : undefined,
        hoofdsom3112: item.hoofdsom3112 ? Math.round(item.hoofdsom3112 * (100 - item.percentageBox1) / 100) : undefined,
        rente: item.rente ? Math.round(item.rente * (100 - item.percentageBox1) / 100) : undefined,
        percentageBox1: 0,
        aanwending: LeningAanwending.OVERIG,
        landCode: item.landCode ?? 'NLD',
        geldVerstrekker: null,
      }));


    items.forEach(item => {
      const bestaatAl = this.box3Leningen.controls.some(control =>
        control.get('omschrijving')?.value === item.omschrijving &&
        control.get('rekeningNummer')?.value === item.rekeningNummer
      );

      if (!bestaatAl) {
        this.box3Leningen.push(this.createFormGroup(item), { emitEvent: false });
      }
    })
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    const items: Lening[] = this.formGroup.getRawValue().box3Leningen
      .filter(item => item.bedrag !== 0 && !(item.bedrag == null && item.omschrijving == null && item.aanwending == null))
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
        rekeningNummer: item.rekeningNummer ?? undefined,
        landCode: item.landCode ?? 'NLD',
        wozNummerWaarLeningBijHoort: item.wozNummerWaarLeningBijHoort != null && item.wozNummerWaarLeningBijHoort !== ''
          ? item.wozNummerWaarLeningBijHoort
          : undefined,
        percentageBox1: item.percentageBox1 ?? 0,
        hoofdsom0101: item.hoofdsom0101 ?? undefined,
        hoofdsom3112: item.hoofdsom3112 ?? undefined,
        geldVerstrekker: (item.geldVerstrekker as LeningGeldVerstrekker | null) ?? LeningGeldVerstrekker.BANK,
        aanwending: item.aanwending as LeningAanwending | undefined,
        bijzondereWaardevermindering: item.bijzondereWaardevermindering ?? undefined,
        rente: item.rente ?? undefined
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box3: {
            ...currentEntity.aangifte.box3,
            leningen: items
          }
        }
      } as IncomeTaxEntity;
    });
  }

  box3WoningValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const aanwending = group.get('aanwending')?.value;
      const wozNummer = group.get('wozNummerWaarLeningBijHoort')?.value;
      const landCode = group.get('landCode')?.value;
      const isBox3Woning = aanwending === LeningAanwending.WONING_NEDERLAND || aanwending === LeningAanwending.WONING_BUITENLAND;

      let hasErrors = false;

      if (isBox3Woning && (wozNummer == null || wozNummer === '')) {
        group.get('wozNummerWaarLeningBijHoort')?.setErrors({required: true});
        hasErrors = true;
      } else {
        const wozControl = group.get('wozNummerWaarLeningBijHoort');
        if (wozControl?.hasError('required') && !isBox3Woning) {
          wozControl.setErrors(null);
        }
      }

      if (isBox3Woning && (landCode == null || landCode === '')) {
        group.get('landCode')?.setErrors({required: true});
        hasErrors = true;
      } else {
        const landControl = group.get('landCode');
        if (landControl?.hasError('required') && !isBox3Woning) {
          landControl.setErrors(null);
        }
      }

      return hasErrors ? {box3WoningRequired: true} : null;
    };
  }

  getOWRDialogTitel(i: number): string {
    const omschrijving = this.box3Leningen.controls[i]?.get('omschrijving')?.value;
    const rekeningnummer = this.box3Leningen.controls[i]?.get('rekeningNummer')?.value;

    if (!omschrijving || !rekeningnummer) {
      return 'Werkelijk rendement schuld';
    }

    return `Werkelijk rendement: ${omschrijving} - ${rekeningnummer}`;
  }

  getBerekendeWerkelijkRendement(i: number): any {
    const control = this.box3Leningen.at(i);
    const bijzondereWaardevermindering = Number(control?.get('bijzondereWaardevermindering')?.value) || 0;
    const rente = Number(control?.get('rente')?.value) || 0;

    if (!control?.get('bijzondereWaardevermindering')?.value && !control?.get('rente')?.value) {
      return null;
    }

    this.box3Leningen.at(i).patchValue({berekendRendement: bijzondereWaardevermindering + rente}, {emitEvent: false});
    return bijzondereWaardevermindering + rente;
  }
}
